import { useState, useEffect, useMemo } from 'react';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { RESTAURANT_ID } from '../constants';
import { printNewOrderReceipt, printCancelReceipt } from '../utils/printer';
import { Empty, Icon, Loading, StatusBadge, tl } from '../ui';
import { OrderDetail, NotifyModal } from './Orders';
import { scheduledLabel } from '../utils/schedule';
import './orders.css';

const shortId = (id) => `#${id.substring(0, 6).toUpperCase()}`;
const toDate = (o) => o.createdAt?.toDate?.() || null;
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const toInput = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fromInput = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };

const PRESETS = [
  { key: 'today', label: 'Bugün' },
  { key: 'yesterday', label: 'Dün' },
  { key: 'week', label: 'Son 7 gün' },
  { key: 'month', label: 'Bu ay' },
  { key: 'lastMonth', label: 'Geçen ay' },
  { key: 'custom', label: 'Tarih seç' },
];

// Seçilen aralık: [from, to) — to hariç.
const presetRange = (key) => {
  const today = startOfDay(new Date());
  if (key === 'yesterday') return [addDays(today, -1), today];
  if (key === 'week') return [addDays(today, -6), addDays(today, 1)];
  if (key === 'month') return [new Date(today.getFullYear(), today.getMonth(), 1), addDays(today, 1)];
  if (key === 'lastMonth') return [new Date(today.getFullYear(), today.getMonth() - 1, 1), new Date(today.getFullYear(), today.getMonth(), 1)];
  return [today, addDays(today, 1)];
};

const csvCell = (v) => {
  const s = String(v ?? '');
  return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const csvMoney = (n) => (Number(n) || 0).toFixed(2).replace('.', ',');

function OrderHistory() {
  const [preset, setPreset] = useState('today');
  const [customFrom, setCustomFrom] = useState(toInput(addDays(new Date(), -6)));
  const [customTo, setCustomTo] = useState(toInput(new Date()));
  const [status, setStatus] = useState('all'); // all | delivered | cancelled
  const [type, setType] = useState('all'); // all | delivery | pickup
  const [payment, setPayment] = useState('all');
  const [search, setSearch] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [notifyTarget, setNotifyTarget] = useState(null);
  const [pageSize, setPageSize] = useState(() => {
    try { return Number(localStorage.getItem('kadihan_history_page_size')) === 50 ? 50 : 25; } catch { return 25; }
  });
  const [page, setPage] = useState(1);

  const [from, to] = useMemo(() => {
    if (preset !== 'custom') return presetRange(preset);
    const a = fromInput(customFrom);
    const b = fromInput(customTo);
    return a <= b ? [a, addDays(b, 1)] : [b, addDays(a, 1)];
  }, [preset, customFrom, customTo]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const snap = await getDocs(query(collection(db, 'orders'),
          where('restaurantId', '==', RESTAURANT_ID),
          where('status', 'in', ['delivered', 'cancelled']),
          where('createdAt', '>=', Timestamp.fromDate(from)),
          where('createdAt', '<', Timestamp.fromDate(to))));
        if (cancelled) return;
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (toDate(b) || 0) - (toDate(a) || 0));
        setOrders(list);
      } catch (e) {
        console.error(e);
        if (!cancelled) setError('Siparişler yüklenemedi. Sayfayı yenileyip tekrar deneyin.');
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [from, to]);

  useEffect(() => { setPage(1); }, [from, to, status, type, payment, search, pageSize]);

  const paymentOptions = useMemo(() => [...new Set(orders.map((o) => o.paymentMethod).filter(Boolean))].sort(), [orders]);

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('tr-TR');
    return orders.filter((o) => (status === 'all' || o.status === status)
      && (type === 'all' || (type === 'pickup' ? o.orderType === 'pickup' : o.orderType !== 'pickup'))
      && (payment === 'all' || o.paymentMethod === payment)
      && (!q || [o.id, shortId(o.id), o.userName, o.userPhone, o.addressPhone]
        .some((f) => String(f || '').toLocaleLowerCase('tr-TR').includes(q))));
  }, [orders, status, type, payment, search]);

  const delivered = filtered.filter((o) => o.status === 'delivered');
  const revenue = delivered.reduce((s, o) => s + (Number(o.totalPrice) || 0), 0);
  const cancelledCount = filtered.length - delivered.length;

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageStart = (currentPage - 1) * pageSize;
  const paged = filtered.slice(pageStart, pageStart + pageSize);
  const selected = filtered.find((o) => o.id === selectedId) || null;

  const changePageSize = (n) => {
    setPageSize(n);
    try { localStorage.setItem('kadihan_history_page_size', String(n)); } catch { /* yok say */ }
  };

  const rangeLabel = `${from.toLocaleDateString('tr-TR')} – ${addDays(to, -1).toLocaleDateString('tr-TR')}`;

  const exportCsv = () => {
    const head = ['Tarih', 'Saat', 'Sipariş no', 'Durum', 'Müşteri', 'Telefon', 'Tür', 'Bölge', 'Adres', 'Ödeme',
      'İndirim (TL)', 'Kupon', 'Teslimat ücreti (TL)', 'Toplam (TL)', 'Ürünler', 'İptal nedeni'];
    const rows = filtered.map((o) => {
      const d = toDate(o);
      return [
        d ? d.toLocaleDateString('tr-TR') : '',
        d ? d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '',
        shortId(o.id),
        o.status === 'delivered' ? 'Teslim edildi' : 'İptal',
        o.userName || '',
        o.addressPhone || o.userPhone || '',
        o.orderType === 'pickup' ? 'Gel-al' : 'Teslimat',
        o.zoneName || '',
        o.orderType === 'pickup' ? '' : (o.address || ''),
        o.paymentMethod || '',
        csvMoney(o.discount),
        o.appliedCouponCode || '',
        csvMoney(o.deliveryFee),
        csvMoney(o.totalPrice),
        (o.items || []).map((it) => `${it.quantity}x ${it.name}`).join(', '),
        o.cancelReason || '',
      ];
    });
    const csv = '﻿' + [head, ...rows].map((r) => r.map(csvCell).join(';')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `kadihan-siparisler_${toInput(from)}_${toInput(addDays(to, -1))}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="k-page ko-page">
      <div className="k-card kh-filters">
        <div className="kh-row">
          <div className="k-seg">
            {PRESETS.map((p) => (
              <button key={p.key} type="button" className={preset === p.key ? 'on' : ''} onClick={() => setPreset(p.key)}>{p.label}</button>
            ))}
          </div>
          {preset === 'custom' && (
            <div className="kh-dates">
              <input type="date" className="k-input" value={customFrom} max={toInput(new Date())} onChange={(e) => e.target.value && setCustomFrom(e.target.value)} aria-label="Başlangıç tarihi" />
              <span>–</span>
              <input type="date" className="k-input" value={customTo} max={toInput(new Date())} onChange={(e) => e.target.value && setCustomTo(e.target.value)} aria-label="Bitiş tarihi" />
            </div>
          )}
        </div>
        <div className="kh-row">
          <select className="k-select" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Durum">
            <option value="all">Tüm durumlar</option>
            <option value="delivered">Teslim edildi</option>
            <option value="cancelled">İptal</option>
          </select>
          <select className="k-select" value={type} onChange={(e) => setType(e.target.value)} aria-label="Tür">
            <option value="all">Teslimat ve gel-al</option>
            <option value="delivery">Sadece teslimat</option>
            <option value="pickup">Sadece gel-al</option>
          </select>
          <select className="k-select" value={payment} onChange={(e) => setPayment(e.target.value)} aria-label="Ödeme">
            <option value="all">Tüm ödeme yöntemleri</option>
            {paymentOptions.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <div className="k-search kh-search">
            <Icon name="search" size={15} />
            <input className="k-input" placeholder="Sipariş no, müşteri, telefon" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="ko-summary kh-summary">
        <span>{rangeLabel}</span>
        <span>Sipariş <b className="tnum">{filtered.length}</b></span>
        <span>Teslim edilen <b className="tnum">{delivered.length}</b></span>
        <span>İptal <b className="tnum">{cancelledCount}</b></span>
        <span>Ciro <b className="tnum">{tl(revenue)}</b></span>
        <button type="button" className="k-btn k-btn-secondary k-btn-sm kh-export" onClick={exportCsv} disabled={loading || filtered.length === 0}>
          <Icon name="download" size={14} />Excel'e aktar
        </button>
      </div>

      <div className="ko-split">
        <div className="k-card ko-list">
          {loading ? <Loading text="Siparişler yükleniyor…" /> : error ? (
            <div className="k-alert error" style={{ margin: 16 }}>{error}</div>
          ) : filtered.length === 0 ? (
            <Empty icon="history" title="Bu aralıkta sipariş yok">Tarih aralığını veya filtreleri değiştirmeyi deneyin.</Empty>
          ) : (
            <>
              <table className="k-table ko-table">
                <thead>
                  <tr><th>Tarih</th><th>No</th><th>Müşteri</th><th>Tür</th><th>Ödeme</th><th style={{ textAlign: 'right' }}>Tutar</th><th>Durum</th></tr>
                </thead>
                <tbody>
                  {paged.map((o) => (
                    <tr key={o.id} className={`clickable${o.id === selectedId ? ' is-selected' : ''}`} onClick={() => setSelectedId(o.id)}>
                      <td className="tnum">{toDate(o)?.toLocaleString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) || '—'}</td>
                      <td className="tnum ko-no">{shortId(o.id)}</td>
                      <td>{o.userName || '—'}</td>
                      <td>{o.orderType === 'pickup' ? 'Gel-al' : 'Teslimat'}{scheduledLabel(o) ? ` · İleri saat ${scheduledLabel(o)}` : ''}</td>
                      <td>{o.paymentMethod || '—'}</td>
                      <td className="tnum" style={{ textAlign: 'right', fontWeight: 700 }}>{tl(o.totalPrice)}</td>
                      <td><StatusBadge status={o.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="km-pager">
                <span className="tnum">{pageStart + 1}–{pageStart + paged.length} / {filtered.length} sipariş</span>
                <div className="km-pager-pages">
                  <button type="button" className="k-btn k-btn-secondary k-btn-sm" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>Önceki</button>
                  <span className="tnum kh-page">{currentPage} / {pageCount}</span>
                  <button type="button" className="k-btn k-btn-secondary k-btn-sm" disabled={currentPage === pageCount} onClick={() => setPage(currentPage + 1)}>Sonraki</button>
                </div>
                <label className="km-pager-size">
                  Sayfa başına
                  <select className="k-select" value={pageSize} onChange={(e) => changePageSize(Number(e.target.value))}>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </label>
              </div>
            </>
          )}
        </div>

        <aside className="k-card ko-detail">
          {selected ? (
            <OrderDetail
              order={selected} now={new Date()} isNewCustomer={false}
              countdown={null} action={null} busy={false}
              onAdvance={() => {}} onCancel={() => {}}
              onNotify={() => setNotifyTarget(selected)}
              onPrint={() => printNewOrderReceipt(selected, {})}
              onPrintCancel={() => printCancelReceipt(selected)}
            />
          ) : (
            <Empty icon="orders" title="Sipariş seçin">Ayrıntıları görmek için listeden bir siparişe tıklayın.</Empty>
          )}
        </aside>
      </div>

      {notifyTarget && <NotifyModal order={notifyTarget} onClose={() => setNotifyTarget(null)} />}

      <style>{`
        .kh-filters { padding: 12px 14px; display: grid; gap: 10px; margin-bottom: 12px; }
        .kh-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .kh-row .k-select { width: auto; min-width: 180px; }
        .kh-dates { display: flex; align-items: center; gap: 6px; }
        .kh-dates .k-input { width: 150px; }
        .kh-search { flex: 1; min-width: 220px; }
        .kh-summary { align-items: center; }
        .kh-export { margin-left: auto; }
        .kh-page { padding: 0 8px; font-size: 13px; }
      `}</style>
    </div>
  );
}

export default OrderHistory;
